import { $createCodeNode } from '@lexical/code-core';
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
} from '@lexical/list';
import {
  $createHeadingNode,
  $createQuoteNode,
  type HeadingTagType,
} from '@lexical/rich-text';
import { $patchStyleText, $setBlocksType } from '@lexical/selection';
import { $getNearestBlockElementAncestorOrThrow } from '@lexical/utils';
import {
  $addUpdateTag,
  $createParagraphNode,
  $getSelection,
  $isElementNode,
  $isRangeSelection,
  $isTextNode,
  type LexicalEditor,
  type LexicalNode,
  SKIP_DOM_SELECTION_TAG,
  SKIP_SELECTION_FOCUS_TAG,
  type TextNode,
} from 'lexical';

import {
  DEFAULT_FONT_SIZE,
  MAX_ALLOWED_FONT_SIZE,
  MIN_ALLOWED_FONT_SIZE,
} from '../context/ToolbarContext';

export enum UpdateFontSizeType {
  increment = 1,
  decrement,
}

/**
 * Calculates the new font size based on the update type.
 * @param currentFontSize - The current font size
 * @param updateType - The type of change, either increment or decrement
 * @returns the next font size
 */
export const calculateNextFontSize = (
  currentFontSize: number,
  updateType: UpdateFontSizeType | null,
) => {
  if (!updateType) {
    return currentFontSize;
  }

  let updatedFontSize: number = currentFontSize;
  switch (updateType) {
    case UpdateFontSizeType.decrement:
      switch (true) {
        case currentFontSize > MAX_ALLOWED_FONT_SIZE:
          updatedFontSize = MAX_ALLOWED_FONT_SIZE;
          break;
        case currentFontSize >= 48:
          updatedFontSize -= 12;
          break;
        case currentFontSize >= 24:
          updatedFontSize -= 4;
          break;
        case currentFontSize >= 14:
          updatedFontSize -= 2;
          break;
        case currentFontSize >= 9:
          updatedFontSize -= 1;
          break;
        default:
          updatedFontSize = MIN_ALLOWED_FONT_SIZE;
          break;
      }
      break;

    case UpdateFontSizeType.increment:
      switch (true) {
        case currentFontSize < MIN_ALLOWED_FONT_SIZE:
          updatedFontSize = MIN_ALLOWED_FONT_SIZE;
          break;
        case currentFontSize < 12:
          updatedFontSize += 1;
          break;
        case currentFontSize < 20:
          updatedFontSize += 2;
          break;
        case currentFontSize < 36:
          updatedFontSize += 4;
          break;
        case currentFontSize <= 60:
          updatedFontSize += 12;
          break;
        default:
          updatedFontSize = MAX_ALLOWED_FONT_SIZE;
          break;
      }
      break;

    default:
      break;
  }
  return updatedFontSize;
};

/**
 * Patches the selection with the updated font size.
 */
export const updateFontSizeInSelection = (
  editor: LexicalEditor,
  newFontSize: string | null,
  updateType: UpdateFontSizeType | null,
  skipRefocus: boolean,
) => {
  const getNextFontSize = (prevFontSize: string | null): string => {
    if (!prevFontSize) {
      prevFontSize = `${DEFAULT_FONT_SIZE}px`;
    }
    prevFontSize = prevFontSize.slice(0, -2);
    const nextFontSize = calculateNextFontSize(
      Number(prevFontSize),
      updateType,
    );
    return `${nextFontSize}px`;
  };

  editor.update(() => {
    if (skipRefocus) {
      $addUpdateTag(SKIP_DOM_SELECTION_TAG);
    }
    if (editor.isEditable()) {
      const selection = $getSelection();
      if (selection !== null) {
        $patchStyleText(selection, {
          'font-size': newFontSize || getNextFontSize,
        });
      }
    }
  });
};

export const updateFontSize = (
  editor: LexicalEditor,
  updateType: UpdateFontSizeType,
  inputValue: string,
  skipRefocus: boolean = false,
) => {
  if (inputValue !== '') {
    const nextFontSize = calculateNextFontSize(Number(inputValue), updateType);
    updateFontSizeInSelection(
      editor,
      String(nextFontSize) + 'px',
      null,
      skipRefocus,
    );
  } else {
    updateFontSizeInSelection(editor, null, updateType, skipRefocus);
  }
};

export const formatParagraph = (editor: LexicalEditor) => {
  editor.update(() => {
    $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
    const selection = $getSelection();
    $setBlocksType(selection, () => $createParagraphNode());
  });
};

export const formatHeading = (
  editor: LexicalEditor,
  blockType: string,
  headingSize: HeadingTagType,
) => {
  if (blockType !== headingSize) {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      const selection = $getSelection();
      $setBlocksType(selection, () => $createHeadingNode(headingSize));
    });
  }
};

export const formatBulletList = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'bullet') {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
    });
  } else {
    formatParagraph(editor);
  }
};

export const formatNumberedList = (
  editor: LexicalEditor,
  blockType: string,
) => {
  if (blockType !== 'number') {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
    });
  } else {
    formatParagraph(editor);
  }
};

export const formatQuote = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'quote') {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      const selection = $getSelection();
      $setBlocksType(selection, () => $createQuoteNode());
    });
  }
};

export const formatCode = (editor: LexicalEditor, blockType: string) => {
  if (blockType !== 'code') {
    editor.update(() => {
      $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
      let selection = $getSelection();
      if (!selection) {
        return;
      }
      if (!$isRangeSelection(selection) || selection.isCollapsed()) {
        $setBlocksType(selection, () => $createCodeNode());
      } else {
        const textContent = selection.getTextContent();
        const codeNode = $createCodeNode();
        selection.insertNodes([codeNode]);
        selection = $getSelection();
        if ($isRangeSelection(selection)) {
          selection.insertRawText(textContent);
        }
      }
    });
  }
};

export const clearFormatting = (
  editor: LexicalEditor,
  skipRefocus: boolean = false,
) => {
  editor.update(() => {
    if (skipRefocus) {
      $addUpdateTag(SKIP_DOM_SELECTION_TAG);
    }
    $addUpdateTag(SKIP_SELECTION_FOCUS_TAG);
    const selection = $getSelection();

    if (!$isRangeSelection(selection)) {
      return;
    }

    // Step 1: Clear all text formatting using $patchStyleText (clears all inline styles at once)
    // Do this BEFORE $setBlocksType to avoid selection invalidation
    try {
      $patchStyleText(selection, {
        'font-size': null,
        'font-family': null,
        'font-weight': null,
        color: null,
        'background-color': null,
        'text-decoration': null,
      });
    } catch {
      // If selection is invalid, skip this step - we'll clear styles directly on nodes later
    }

    // Step 2: Get all nodes we need to process BEFORE modifying structure
    const nodes = selection.getNodes();
    const extractedNodes = selection.extract();

    // Step 3: Convert all block types to paragraphs (handles headings, quotes, code, lists)
    // This may invalidate the selection, so we need to get a fresh selection after this
    try {
      $setBlocksType(selection, () => $createParagraphNode());
    } catch {
      // If selection is invalid, skip this step
    }

    // Step 4: Get a fresh selection after structural changes
    const freshSelection = $getSelection();
    if (!$isRangeSelection(freshSelection)) {
      // If we don't have a valid selection, try to work with the nodes we collected
      // Fall back to using the original nodes
    }

    // Step 5: Clear all text formats (bold, italic, underline, strikethrough, subscript, superscript, etc.)
    // Use the fresh selection if available, otherwise use the original nodes
    let nodesToProcess = nodes;
    let extractedNodesToProcess = extractedNodes;
    let anchorOffset = 0;
    let focusOffset = 0;

    if ($isRangeSelection(freshSelection)) {
      try {
        const freshNodes = freshSelection.getNodes();
        const freshExtractedNodes = freshSelection.extract();
        nodesToProcess = freshNodes.length > 0 ? freshNodes : nodes;
        extractedNodesToProcess =
          freshExtractedNodes.length > 0 ? freshExtractedNodes : extractedNodes;
        anchorOffset = freshSelection.anchor.offset;
        focusOffset = freshSelection.focus.offset;
      } catch {
        // If fresh selection is invalid, use original nodes
      }
    }

    // Collect all text nodes that need formatting cleared (including descendants)
    const textNodesToClear = new Set<TextNode>();

    // Helper function to recursively collect all text nodes from a node and its descendants
    const collectTextNodes = (node: LexicalNode) => {
      if ($isTextNode(node)) {
        textNodesToClear.add(node);
      } else if ($isElementNode(node)) {
        // Get all children and recursively collect text nodes
        const children = node.getChildren();
        children.forEach((child) => {
          if ($isTextNode(child)) {
            textNodesToClear.add(child);
          } else if ($isElementNode(child)) {
            // Recursively collect from nested elements
            collectTextNodes(child);
          }
        });
      }
    };

    // Collect text nodes from all nodes we collected
    nodesToProcess.forEach((node, idx) => {
      if ($isTextNode(node)) {
        // Try to split text nodes at selection boundaries if we have valid offsets
        let textNode = node;
        try {
          if (idx === 0 && anchorOffset !== 0) {
            const splitResult = textNode.splitText(anchorOffset);
            textNode = splitResult[1] || textNode;
          }
          if (idx === nodesToProcess.length - 1) {
            const splitResult = textNode.splitText(focusOffset);
            textNode = splitResult[0] || textNode;
          }
        } catch {
          // If splitting fails, just use the original node
          // This can happen if the node structure changed
        }

        textNodesToClear.add(textNode);
        // Also collect any descendants (in case text node contains nested elements)
        collectTextNodes(textNode);
      } else {
        // For element nodes, collect all text node descendants
        collectTextNodes(node);
      }
    });

    // Also check extracted nodes for any additional text nodes we might have missed
    extractedNodesToProcess.forEach((node) => {
      collectTextNodes(node);
    });

    // Clear all formats and styles on all collected text nodes
    // This ensures subscript and superscript are cleared along with all other formats
    textNodesToClear.forEach((textNode) => {
      // Clear all text format flags (bold, italic, underline, strikethrough, subscript, superscript, code)
      // Setting format to 0 clears ALL format flags including subscript and superscript
      const currentFormat = textNode.getFormat();
      if (currentFormat !== 0) {
        textNode.setFormat(0);
      }
      // Clear all text styles (font-size, color, etc.)
      const currentStyle = textNode.getStyle();
      if (currentStyle !== '') {
        textNode.setStyle('');
      }
    });

    // Step 4: Clear all block-level formatting (indent, alignment) for all block elements
    const blockNodes = new Set<
      ReturnType<typeof $getNearestBlockElementAncestorOrThrow>
    >();
    nodes.forEach((node) => {
      if ($isTextNode(node)) {
        const block = $getNearestBlockElementAncestorOrThrow(node);
        blockNodes.add(block);
      } else if ($isElementNode(node)) {
        blockNodes.add(node);
      }
    });

    blockNodes.forEach((block) => {
      if ($isElementNode(block)) {
        // Clear element format (alignment: left, center, right, justify)
        if (block.__format !== 0) {
          block.setFormat('');
        }
        // Clear indent
        if (block.__indent !== 0) {
          block.setIndent(0);
        }
      }
    });
  });
};

export const transformText = (
  editor: LexicalEditor,
  transformFn: (text: string) => string,
) => {
  editor.update(() => {
    const selection = $getSelection();
    if (!$isRangeSelection(selection)) {
      return;
    }

    const selectedText = selection.getTextContent();

    if (selectedText === '') {
      return;
    }

    const transformedText = transformFn(selectedText);

    // Replace the selected text with transformed text
    selection.insertText(transformedText);
  });
};

export const transformToLowercase = (editor: LexicalEditor) => {
  transformText(editor, (text) => text.toLowerCase());
};

export const transformToUppercase = (editor: LexicalEditor) => {
  transformText(editor, (text) => text.toUpperCase());
};

export const transformToCapitalize = (editor: LexicalEditor) => {
  transformText(editor, (text) => {
    return text
      .toLowerCase()
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  });
};
